import { Popover } from '@base-ui/react/popover';
import { useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ExerciseLibraryFilterTabs,
  exerciseFacetGroups,
  muscleGroupLabelKey,
  type ExerciseFacetLabels,
  type ExerciseLibraryFilterLabels,
} from '~/entities/exercise-library';
import { useSessionUser } from '~/entities/session';
import { displayInitials } from '~/shared/lib/displayInitials';
import type { SkeletonGate } from '~/shared/lib/useSkeletonGate';
import { EmptyState } from '~/shared/ui/EmptyState';
import { DumbbellMarkIcon } from '~/shared/ui/EmptyStateIcons';
import { FilterIcon } from '~/shared/ui/FilterIcon';
import square from '~/shared/ui/filterSquare.module.css';
import ghost from '~/shared/ui/ghostButton.module.css';
import inlineButton from '~/shared/ui/inlineTextButton.module.css';
import photo from '~/shared/ui/photoBox.module.css';
import { SearchField } from '~/shared/ui/SearchField';
import skel from '~/shared/ui/skeleton.module.css';
import stateAction from '~/shared/ui/stateAction.module.css';
import { ChevronGlyph, FilmGlyph } from '~/shared/ui/VideoGlyphs';
import hidden from '~/shared/ui/visuallyHidden.module.css';

import type { FormaState } from '../lib/useForma';
import type { FormaExerciseRow } from '../model/formaExercises';

import styles from './Forma.module.css';

const SKELETON_ROWS = 6;

/**
 * The exercise library's own words for its search and its filter panel, as the survey editor's
 * picker reuses them: the same field and the same four facets should read the same everywhere.
 */
const LIB = 'web:planBuilder.rail';

interface FormaExerciseListProps {
  readonly rows: readonly FormaExerciseRow[];
  readonly selected: number | null;
  readonly onSelect: (exerciseId: number) => void;
  readonly gate: SkeletonGate;
  readonly failed: boolean;
  readonly onRetry: () => void;
  /** „Z nagraniem": the list shows only the movements with a recording. */
  readonly withClipOnly: boolean;
  readonly onToggleWithClipOnly: () => void;
  /** „‹ Wszystkie nagrania", while the gallery is narrowed to one movement. */
  readonly onBack: (() => void) | null;
  /** The library's search and facets over this client's movements. */
  readonly search: FormaState['list']['search'];
}

export function FormaExerciseList({
  rows,
  selected,
  onSelect,
  gate,
  failed,
  onRetry,
  withClipOnly,
  onToggleWithClipOnly,
  onBack,
  search,
}: FormaExerciseListProps) {
  const { t } = useTranslation();
  const user = useSessionUser();
  const chip = useRef<HTMLButtonElement>(null);
  const body = useRef<HTMLDivElement>(null);
  const head = useRef<HTMLDivElement>(null);
  const field = useRef<HTMLInputElement>(null);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const filtersId = useId();

  const facets: ExerciseFacetLabels = {
    group: (facet) => t(`${LIB}.facet.${facet}`),
    muscle: (muscle) => t(muscleGroupLabelKey(muscle)),
    type: (type) => t(`web:planBuilder.day.exerciseType.${type}`),
    origin: (origin) => t(`${LIB}.origin.${origin}`),
    everything: t(`${LIB}.anyValue`),
  };
  const panel: ExerciseLibraryFilterLabels = {
    tab: (tab) => t(`${LIB}.tab.${tab}`),
    tabWithCount: (label, count) => t(`${LIB}.tabWithCount`, { label, count }),
    panel: t(`${LIB}.filters`),
    optionName: (label, count) => t(`${LIB}.facetOption`, { label, count }),
    optionSearch: (facet) => t(`${LIB}.optionSearch.${facet}`),
    originHint: (id) => t(`${LIB}.originHint.${id}`),
    empty: t(`${LIB}.facetEmpty`),
    clear: t(`${LIB}.clearShort`),
    apply: t(`${LIB}.apply`),
  };
  const active = search.catalogue.activeCount;

  return (
    <nav aria-busy={gate.waiting} aria-label={t('web:forma.list.label')} className={styles.list}>
      <div className={styles.listTop} ref={head}>
        <div className={styles.listHead}>
          <h2 className={styles.listTitle}>{t('web:forma.list.title')}</h2>
          <Popover.Root open={filtersOpen} onOpenChange={setFiltersOpen}>
            {/* The library toolbar's filter square: the count rides in the name, the dot is its echo. */}
            <Popover.Trigger
              aria-label={active === 0 ? t(`${LIB}.filters`) : t(`${LIB}.filtersWithCount`, { count: active })}
              className={`${String(square.square)} ${String(styles.listFilter)}`}
              title={t(`${LIB}.filters`)}
            >
              <FilterIcon className={square.icon} />
              {active > 0 && <span aria-hidden className={square.dot} />}
            </Popover.Trigger>
            <Popover.Portal>
              <Popover.Positioner
                align="start"
                anchor={head}
                className={styles.listFilterPositioner}
                side="bottom"
                sideOffset={6}
              >
                <Popover.Popup className={styles.listFilterPopup}>
                  <ExerciseLibraryFilterTabs
                    groups={exerciseFacetGroups(search.catalogue, facets)}
                    id={filtersId}
                    labels={panel}
                    onApply={() => {
                      setFiltersOpen(false);
                    }}
                    onClear={search.clear}
                    onRemove={search.remove}
                    onToggle={search.toggle}
                    resultCount={rows.length}
                    trainer={{
                      initials: displayInitials(user?.displayName ?? ''),
                      avatarUrl: user?.avatarUrl ?? null,
                    }}
                  />
                </Popover.Popup>
              </Popover.Positioner>
            </Popover.Portal>
          </Popover.Root>
          <button
            aria-pressed={withClipOnly}
            className={`${String(styles.pressChip)} ${String(styles.filterChip)} ${withClipOnly ? String(styles.pressChipOn) : ''}`}
            onClick={onToggleWithClipOnly}
            ref={chip}
            type="button"
          >
            <FilmGlyph className={styles.filterChipGlyph} />
            {t('web:forma.list.withClip')}
          </button>
        </div>
        <SearchField
          className={styles.listSearch}
          clearLabel={t('web:exerciseLibrary.clearSearch')}
          disabled={failed}
          inputRef={field}
          label={t(`${LIB}.search`)}
          onChange={search.setText}
          placeholder={t(`${LIB}.searchPlaceholder`)}
          size="compact"
          value={search.query.text}
        />
      </div>
      {onBack === null ? null : (
        <button
          className={styles.listBack}
          onClick={() => {
            onBack();
            // The row list stands across the unfiltering, so its first row is there to take focus.
            (body.current?.querySelector('button') ?? chip.current)?.focus();
          }}
          type="button"
        >
          <ChevronGlyph className={styles.listBackGlyph} direction="left" />
          {t('web:forma.list.allRecordings')}
        </button>
      )}
      {gate.waiting ? (
        <>
          <span className={hidden.srOnly}>{t('web:clientDetail.states.loading')}</span>
          {gate.skeleton
            ? Array.from({ length: SKELETON_ROWS }, (_, index) => (
                <span className={styles.listRowWait} key={index}>
                  <i className={`${String(skel.bar)} ${String(styles.skelThumb)}`} />
                  <i className={`${String(skel.bar)} ${String(styles.skelLine)}`} />
                </span>
              ))
            : null}
        </>
      ) : (
        <div className={`${String(styles.listBody)} ${gate.reveal}`} ref={body}>
          {failed ? (
            <p className={styles.listNotice}>
              {t('web:forma.list.failed')}{' '}
              <button className={inlineButton.inlineTextButton} onClick={onRetry} type="button">
                {t('web:clientDetail.states.retry')}
              </button>
            </p>
          ) : rows.length === 0 ? (
            <div className={styles.listEmpty}>
              <EmptyState
                hint={search.narrowedBy === 'query' ? t(`${LIB}.noMatchesHint`) : undefined}
                mark={<DumbbellMarkIcon />}
                title={
                  search.narrowedBy === 'query'
                    ? t(`${LIB}.noMatches`)
                    : t('web:forma.list.emptyFiltered')
                }
                tone="neutral"
              />
              <button
                className={`${String(ghost.ghost)} ${String(stateAction.stateAction)}`}
                onClick={() => {
                  search.clearAll();
                  // The button leaves with the empty list; the field it reset takes the focus.
                  field.current?.focus();
                }}
                type="button"
              >
                {t('web:clients.states.clearFilters')}
              </button>
            </div>
          ) : (
            <ul className={styles.listRows}>
              {rows.map((row) => (
                <li key={row.exerciseId}>
                  <button
                    aria-current={row.exerciseId === selected ? 'true' : undefined}
                    className={`${String(styles.listRow)} ${row.exerciseId === selected ? String(styles.listRowOn) : ''}`}
                    onClick={() => {
                      onSelect(row.exerciseId);
                    }}
                    type="button"
                  >
                    <span className={styles.listThumb}>
                      {row.coverUrl === null ? null : (
                        <img
                          alt=""
                          className={`${String(photo.photo)} ${String(styles.listThumbImg)}`}
                          decoding="async"
                          loading="lazy"
                          src={row.coverUrl}
                        />
                      )}
                    </span>
                    <span className={styles.listText}>
                      <span className={styles.listName}>
                        <span className={styles.listNameText}>{row.name}</span>
                        {row.hasClip ? (
                          <span className={styles.listGlyph} title={t('web:forma.list.hasClip')}>
                            <FilmGlyph />
                            <span className={hidden.srOnly}>{t('web:forma.list.hasClip')}</span>
                          </span>
                        ) : null}
                        {row.unread ? (
                          <span className={styles.unreadDot}>
                            <span className={hidden.srOnly}>{t('web:forma.list.unread')}</span>
                          </span>
                        ) : null}
                      </span>
                      {row.trained === null ? null : (
                        <span className={styles.listTrained}>{row.trained}</span>
                      )}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </nav>
  );
}
